import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Briefcase, Sparkles, Loader2, Bookmark, BookmarkCheck,
  CheckCircle2, X, ExternalLink, Code2, Trophy, Wrench, GitBranch,
  Clock, MapPin, Flame, ShieldCheck, IndianRupee,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type VerificationStatus = "unverified" | "live" | "expired" | "unreachable";

type VerifyResult = {
  id: string;
  verdict: VerificationStatus;
  summary: string | null;
  note: string;
  salary: string | null;
  delta: number;
  fitScore: number;
};

type Opportunity = {
  id: string;
  opportunity_type: "internship" | "gig" | "hackathon" | "open-source";
  title: string;
  organization: string | null;
  description: string;
  skills: string[];
  duration: string | null;
  location: string | null;
  urgency: string;
  fit_score: number;
  why_fit: string | null;
  link_hint: string | null;
  status: "new" | "saved" | "applied" | "dismissed";
  created_at: string;
  verification_status: VerificationStatus | null;
  verified_at: string | null;
  verified_summary: string | null;
  verification_note: string | null;
  verified_salary: string | null;
  verification_score_delta: number | null;
};

const VERIFY_META: Record<VerificationStatus, { label: string; className: string }> = {
  unverified: { label: "Not checked yet", className: "border-border text-muted-foreground" },
  live: { label: "Checked · still open", className: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" },
  expired: { label: "Closed", className: "border-destructive/40 text-destructive" },
  unreachable: { label: "Could not check", className: "border-amber-500/40 text-amber-600 dark:text-amber-400" },
};

const TYPE_META: Record<Opportunity["opportunity_type"], { label: string; icon: any; color: string }> = {
  internship: { label: "Internship", icon: Briefcase, color: "bg-blue-500/10 text-blue-500" },
  gig: { label: "Gig", icon: Wrench, color: "bg-emerald-500/10 text-emerald-500" },
  hackathon: { label: "Hackathon", icon: Trophy, color: "bg-amber-500/10 text-amber-500" },
  "open-source": { label: "Open Source", icon: GitBranch, color: "bg-purple-500/10 text-purple-500" },
};

import { buildSearchUrl, sourceLabel } from "@/lib/opportunities";

export default function Opportunities() {
  const { user } = useAuth();
  const [items, setItems] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [verifying, setVerifying] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<string>("all");

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("opportunities").select("*").eq("user_id", user.id)
      .neq("status", "dismissed")
      .order("fit_score", { ascending: false })
      .order("created_at", { ascending: false });
    setItems((data as Opportunity[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const discover = async () => {
    setDiscovering(true);
    try {
      const { data, error } = await supabase.functions.invoke("discover-opportunities", { body: {} });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`${data.opportunities.length} new opportunities curated`);
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDiscovering(false);
    }
  };

  const verify = async (ids: string[]) => {
    if (!ids.length) return;
    setVerifying(new Set(ids));
    try {
      const { data, error } = await supabase.functions.invoke("verify-opportunity", { body: { ids } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const results: VerifyResult[] = data?.results ?? [];
      setItems((prev) =>
        prev.map((x) => {
          const r = results.find((v) => v.id === x.id);
          return r
            ? {
                ...x,
                verification_status: r.verdict,
                verification_note: r.note,
                verified_summary: r.summary,
                verified_salary: r.salary,
                verification_score_delta: r.delta,
                fit_score: r.fitScore,
                verified_at: new Date().toISOString(),
              }
            : x;
        }),
      );
      const live = results.filter((r) => r.verdict === "live").length;
      const closed = results.filter((r) => r.verdict === "expired").length;
      toast.success(
        closed > 0
          ? `${live} still open · ${closed} have closed and moved down your list`
          : `${live} still open`,
      );
    } catch (e: any) {
      toast.error(e.message ?? "We could not check those right now");
    } finally {
      setVerifying(new Set());
    }
  };

  const updateStatus = async (id: string, status: Opportunity["status"]) => {
    setItems((prev) =>
      status === "dismissed"
        ? prev.filter((x) => x.id !== id)
        : prev.map((x) => (x.id === id ? { ...x, status } : x))
    );
    await supabase.from("opportunities").update({ status }).eq("id", id);
  };

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "saved") return items.filter((x) => x.status === "saved" || x.status === "applied");
    return items.filter((x) => x.opportunity_type === tab);
  }, [items, tab]);

  const counts = useMemo(() => ({
    all: items.length,
    internship: items.filter((x) => x.opportunity_type === "internship").length,
    gig: items.filter((x) => x.opportunity_type === "gig").length,
    hackathon: items.filter((x) => x.opportunity_type === "hackathon").length,
    "open-source": items.filter((x) => x.opportunity_type === "open-source").length,
    saved: items.filter((x) => x.status === "saved" || x.status === "applied").length,
  }), [items]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/dashboard"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
            </Button>
            <WeKitLogo size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => verify(filtered.slice(0, 8).map((o) => o.id))}
              disabled={verifying.size > 0 || filtered.length === 0}
              variant="outline"
              className="gap-2 rounded-full"
            >
              {verifying.size > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Check what's still open
            </Button>
            <Button onClick={discover} disabled={discovering} className="gap-2 rounded-full">
              {discovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Discover more
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Opportunity Finder</h1>
          <p className="text-muted-foreground mt-2">
            Real internships, gigs, hackathons, and open-source projects matched to your profile.
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Live listings · powered by Firecrawl &amp; GitHub
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="internship">Internships ({counts.internship})</TabsTrigger>
            <TabsTrigger value="gig">Gigs ({counts.gig})</TabsTrigger>
            <TabsTrigger value="hackathon">Hackathons ({counts.hackathon})</TabsTrigger>
            <TabsTrigger value="open-source">Open Source ({counts["open-source"]})</TabsTrigger>
            <TabsTrigger value="saved">
              <BookmarkCheck className="h-3.5 w-3.5 mr-1" /> Saved ({counts.saved})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardContent className="p-12 text-center">
              <Code2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-foreground">
                {items.length === 0 ? "Find your first opportunities" : "Nothing here yet"}
              </h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                YOMA curates internships, paid gigs, hackathons, and open-source projects that match your top traits.
              </p>
              {items.length === 0 && (
                <Button onClick={discover} disabled={discovering} className="mt-6 rounded-full gap-2">
                  {discovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Discover opportunities
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <section className="grid gap-4 sm:grid-cols-2">
            {filtered.map((o) => {
              const meta = TYPE_META[o.opportunity_type];
              const Icon = meta.icon;
              const url = buildSearchUrl(o.link_hint);
              return (
                <Card key={o.id} className="border-border/50 hover:border-primary/30 transition-all flex flex-col">
                  <CardContent className="p-5 space-y-3 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={meta.color + " hover:" + meta.color + " gap-1"}>
                          <Icon className="h-3 w-3" /> {meta.label}
                        </Badge>
                        {o.urgency === "high" && (
                          <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30 gap-1">
                            <Flame className="h-3 w-3" /> Soon
                          </Badge>
                        )}
                        {o.status === "applied" && (
                          <Badge className="text-xs bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20">
                            Applied
                          </Badge>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display text-lg font-bold text-primary leading-none">{o.fit_score}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">fit</div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-display font-semibold text-foreground leading-tight">{o.title}</h3>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {o.organization && (
                          <p className="text-sm text-muted-foreground">{o.organization}</p>
                        )}
                        {sourceLabel(o.link_hint) && (
                          <Badge variant="outline" className="text-[10px] py-0 h-4 text-muted-foreground">
                            {sourceLabel(o.link_hint)}
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-[10px] py-0 h-4 gap-1 ${VERIFY_META[o.verification_status ?? "unverified"].className}`}>
                          {VERIFY_META[o.verification_status ?? "unverified"].label}
                        </Badge>
                      </div>
                    </div>

                    <p className="text-sm text-foreground/80 line-clamp-3">{o.description}</p>

                    {o.why_fit && (
                      <div className="text-xs text-primary/90 bg-primary/5 p-2 rounded-md border border-primary/10">
                        <span className="font-medium">Why it fits:</span> {o.why_fit}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1">
                      {o.skills.slice(0, 5).map((s) => (
                        <Badge key={s} variant="secondary" className="text-xs font-normal">{s}</Badge>
                      ))}
                    </div>

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {o.duration && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {o.duration}</span>}
                      {o.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.location}</span>}
                      {o.verified_salary && <span className="flex items-center gap-1"><IndianRupee className="h-3 w-3" /> {o.verified_salary}</span>}
                    </div>

                    {o.verification_note && (
                      <p className="text-xs text-muted-foreground/90 border-l-2 border-border pl-2">
                        {o.verification_note}
                        {o.verified_at && (
                          <span className="text-muted-foreground/60"> · {new Date(o.verified_at).toLocaleDateString()}</span>
                        )}
                        {!!o.verification_score_delta && (
                          <span className="text-muted-foreground/60">
                            {" "}· fit {o.verification_score_delta > 0 ? "+" : ""}{o.verification_score_delta}
                          </span>
                        )}
                      </p>
                    )}

                    <div className="flex items-center gap-2 pt-2 border-t border-border/50 mt-auto">
                      {url && (
                        <Button asChild size="sm" variant="default"
                          className="rounded-full gap-1.5 flex-1"
                          onClick={() => { if (o.status === "new") updateStatus(o.id, "applied"); }}>
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            Apply <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-9 w-9 shrink-0"
                        aria-label="Check this listing is still open"
                        title="Check this listing is still open"
                        disabled={verifying.has(o.id)}
                        onClick={() => verify([o.id])}
                      >
                        {verifying.has(o.id)
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <ShieldCheck className="h-4 w-4" />}
                      </Button>
                      <Button size="icon" variant="outline" className="h-9 w-9 shrink-0"
                        onClick={() => updateStatus(o.id, o.status === "saved" ? "new" : "saved")}>
                        {o.status === "saved" || o.status === "applied"
                          ? <BookmarkCheck className="h-4 w-4 text-primary" />
                          : <Bookmark className="h-4 w-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => updateStatus(o.id, "dismissed")}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}
